import * as React from 'react'
import { Popover as BasePopover } from '@base-ui/react/popover'
import { ActionPopoverTrigger } from './nested/action-popover-trigger'
import { ActionPopoverContent } from './nested/action-popover-content'
import { ActionPopoverButton } from './nested/action-popover-button'
import { ActionPopoverSeparator } from './nested/action-popover-separator'
import { ActionPopoverGroup } from './nested/action-popover-group'
import { ActionPopoverBody } from './nested/action-popover-body'
import { ActionPopoverScreen } from './nested/action-popover-screen'
import { ActionPopoverPopup } from './nested/action-popover-popup'

interface ActionPopoverContextValue {
  open: boolean
}

const ActionPopoverContext = React.createContext<ActionPopoverContextValue | null>(null)

interface ActionPopoverRootProps extends Omit<BasePopover.Root.Props, 'children'> {
  children?: React.ReactNode
}

function ActionPopover({ children, open: controlledOpen, onOpenChange, ...props }: ActionPopoverRootProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const openTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Use controlled open if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen

  const handleMouseEnter = React.useCallback(() => {
    // Clear any pending close timer
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    // Start open timer (250ms delay)
    openTimerRef.current = setTimeout(() => {
      if (controlledOpen === undefined) {
        setInternalOpen(true)
      }
      onOpenChange?.(true, { open: true } as any)
    }, 250)
  }, [controlledOpen, onOpenChange])

  const handleMouseLeave = React.useCallback(() => {
    // Clear any pending open timer
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }

    // Start close timer (350ms delay)
    closeTimerRef.current = setTimeout(() => {
      if (controlledOpen === undefined) {
        setInternalOpen(false)
      }
      onOpenChange?.(false, { open: false } as any)
    }, 350)
  }, [controlledOpen, onOpenChange])

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current)
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  // No-op handler to prevent Base UI from handling clicks
  // All open/close is managed by hover handlers above
  const ignoreOpenChange = React.useCallback(() => {}, [])

  return (
    <ActionPopoverContext.Provider value={{ open }}>
      <BasePopover.Root open={open} onOpenChange={ignoreOpenChange} {...props}>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {children}
        </div>
      </BasePopover.Root>
    </ActionPopoverContext.Provider>
  )
}

ActionPopover.Trigger = ActionPopoverTrigger
ActionPopover.Content = ActionPopoverContent
ActionPopover.Button = ActionPopoverButton
ActionPopover.Separator = ActionPopoverSeparator
ActionPopover.Group = ActionPopoverGroup
ActionPopover.Body = ActionPopoverBody
ActionPopover.Screen = ActionPopoverScreen
ActionPopover.Popup = ActionPopoverPopup

export { ActionPopover, ActionPopoverContext, ActionPopoverPopup }
